import { useCallback, useMemo, useState } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { cards as cardsApi, type Board, type Card, type ApiResponse } from '../lib/api';
import { getCreatableColumns } from '../lib/move-rules';
import { getBoardColorHex } from '../lib/utils';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { useCommandEnter } from '../hooks/useCommandEnter';
import { useToast } from './Toast';
import type { WorkflowPhase } from '../lib/api';

interface Props {
  boards: Board[];
  workflowsByBoardId: Record<string, WorkflowPhase[] | undefined>;
  /** Pre-selected board when the user opens the modal from a board-colored chip. */
  initialBoardId?: string;
  onClose: () => void;
}

/**
 * Cross-board card creation modal for the Command Center.
 * 1) Pick a board (colored chips)
 * 2) Pick a phase (auto-filled with the first creatable column)
 * 3) Type a description
 *
 * Optimistically inserts the new card into the ['cards', boardId] query cache
 * so it slides into the correct lane immediately; WS will reconcile.
 */
export function QuickCreateCardModal({ boards, workflowsByBoardId, initialBoardId, onClose }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [boardId, setBoardId] = useState<string>(initialBoardId ?? boards[0]?.id ?? '');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  const selectedBoard = useMemo(() => boards.find((b) => b.id === boardId), [boards, boardId]);

  const creatableColumns = useMemo(() => {
    if (!selectedBoard) return [];
    return getCreatableColumns(selectedBoard.columns, workflowsByBoardId[selectedBoard.id]);
  }, [selectedBoard, workflowsByBoardId]);

  const [columnId, setColumnId] = useState<string | undefined>(undefined);
  const effectiveColumnId = columnId ?? creatableColumns[0]?.id;

  useEscapeKey(onClose);

  const createMutation = useMutation({
    mutationFn: (data: { boardId: string; columnId?: string; title?: string; description: string }) =>
      cardsApi.create(data.boardId, {
        columnId: data.columnId,
        title: data.title,
        description: data.description,
      }),
    onSuccess: (res, variables) => {
      const newCard = res.data;
      if (newCard) {
        // Optimistic cache update so the card slides in without waiting for WS.
        queryClient.setQueryData<ApiResponse<Card[]>>(
          ['cards', variables.boardId],
          (old) => {
            if (!old?.data) return old;
            if (old.data.some((c) => c.id === newCard.id)) return old;
            return { ...old, data: [...old.data, newCard] };
          },
        );
      }
      queryClient.invalidateQueries({ queryKey: ['cards', variables.boardId] });
      toast('Card created!', 'success');
      onClose();
    },
    onError: () => toast('Failed to create card', 'error'),
  });

  const canSubmit = !!boardId && (title.trim() || description.trim()) && !createMutation.isPending;

  const submit = useCallback(() => {
    if (!canSubmit) return;
    createMutation.mutate({
      boardId,
      columnId: effectiveColumnId,
      ...(title.trim() ? { title: title.trim() } : {}),
      description: description.trim(),
    });
  }, [canSubmit, boardId, effectiveColumnId, title, description, createMutation]);

  useCommandEnter(submit);

  const handleBackdropClick = () => {
    if (createMutation.isPending) return;
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center md:items-center" onClick={handleBackdropClick}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        onClick={(e) => e.stopPropagation()}
        className="relative z-10 flex w-full flex-col rounded-t-2xl border border-gray-800 bg-surface-850 p-5 shadow-2xl md:max-w-md md:rounded-xl md:p-6"
      >
        <h2 className="mb-4 text-lg font-semibold">New Card</h2>

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
              Board
            </label>
            {boards.length === 0 ? (
              <p className="text-xs text-gray-500">No boards — create one from the dashboard first.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {boards.map((b) => {
                  const selected = b.id === boardId;
                  const hex = getBoardColorHex(b.settings?.color as string | undefined);
                  return (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => {
                        setBoardId(b.id);
                        setColumnId(undefined);
                      }}
                      className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                        selected
                          ? 'border-primary-500 bg-primary-500/10 text-gray-100'
                          : 'border-gray-700 bg-surface-900 text-gray-400 hover:bg-gray-800 hover:text-gray-200'
                      }`}
                    >
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: hex }} />
                      {b.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {creatableColumns.length > 1 && (
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
                Phase
              </label>
              <div className="flex flex-wrap gap-2">
                {creatableColumns.map((col) => (
                  <button
                    key={col.id}
                    type="button"
                    onClick={() => setColumnId(col.id)}
                    className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                      effectiveColumnId === col.id
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                    }`}
                  >
                    {col.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
              Title <span className="font-normal normal-case text-gray-600">(optional)</span>
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-lg border border-gray-700 bg-surface-900 px-3 py-2.5 text-base text-gray-100 outline-none focus:border-primary-500 md:py-2 md:text-sm"
              placeholder="What needs to be done?"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              className="w-full resize-none rounded-lg border border-gray-700 bg-surface-900 px-3 py-2.5 text-base text-gray-100 outline-none focus:border-primary-500 md:py-2 md:text-sm"
              placeholder="Describe the task in detail…"
              autoFocus
            />
          </div>
        </div>

        <div className="mt-5 flex gap-3 md:mt-6 md:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={createMutation.isPending}
            className="flex-1 rounded-lg px-4 py-2.5 text-sm font-medium text-gray-400 hover:bg-gray-800 hover:text-gray-200 disabled:opacity-40 md:flex-none md:py-2"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="flex-1 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-500 disabled:opacity-40 md:flex-none md:py-2"
          >
            {createMutation.isPending ? 'Creating…' : 'Create Card'}
          </button>
        </div>
      </form>
    </div>
  );
}
